<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    padding: {
        type: String,
        default: ''
    },
    placeholderHtml: { type: String, default: '' },
    rows: { type: Number, default: 6 },
    height: { type: String, default: '2.4rem' },
    borderNone: { type: Boolean, default: false }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const contractTermData = ref([]);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);

const filterOptions = () => {
    const q = (searchQuery.value || '').trim().toLowerCase();
    if (!q) {
        filteredOptions.value = contractTermData.value || [];
        return;
    }
    filteredOptions.value = (contractTermData.value || []).filter((opt) => {
        const t = (opt.title || '').toLowerCase();
        const b = (opt.body || '').toLowerCase();
        return t.includes(q) || b.includes(q);
    });
};

const shorten = (s, max = 120) => {
    if (!s) return '';
    const text = String(s).replace(/\s+/g, ' ').trim();
    return text.length > max ? text.slice(0, max) + '...' : text;
};

const searchContractTerm = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/contract/contract-term', config, {
            immediate: false,
            onSuccess(response) {
                const list = (response?.data?.data ?? []).map((item, idx) => {
                    const body = String(item.body ?? '');
                    return {
                        key: `${item.contract_term_id}||${idx}`,
                        id: Number(item.contract_term_id),
                        title: String(item.title ?? ''),
                        body,
                        shortBody: shorten(body)
                    };
                });
                contractTermData.value = list;
                filteredOptions.value = list;
            },
            onError(err) {
                console.error('Error:', err?.message);
                contractTermData.value = [];
                filteredOptions.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        contractTermData.value = [];
        filteredOptions.value = [];
    }
};

onMounted(async () => {
    await searchContractTerm();
    nextTick(() => {
        filteredOptions.value = contractTermData.value;
    });
});

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };

    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) value = value.slice(0, props.maxlength);
    if (props.upper) value = value.toUpperCase();

    cursorPosition = Math.min(cursorPosition, value.length);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? '' : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value
    });
};

const togglePopup = () => {
    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

const fetchContractTermDetail = async (id) => {
    let detail = null;
    try {
        const { execute } = useAxios(
            `/api/contract/contract-term/${id}`,
            { method: 'get' },
            {
                immediate: false,
                onSuccess(resp) {
                    const arr = resp?.data?.data ?? [];
                    if (arr.length) {
                        const it = arr[0];
                        detail = {
                            id: Number(it.contract_term_id),
                            title: it.title || '',
                            body: it.body || ''
                        };
                    }
                },
                onError(err) {
                    console.error('Detail fetch error:', err?.message);
                }
            }
        );
        await execute();
    } catch (e) {
        console.error('Detail fetch exception:', e);
    }
    return detail;
};

const selectValue = (option) => {
    const body = option.body || '';
    inputValue.value = body;

    emit('update:modelValue', body);

    emit('input', {
        id: props.inputId,
        value: body,
        isSelected: true,
        contract_term_id: option.id,
        title: option.title,
        body
    });

    ValidationMsg.value = '';
    closePopup();
};

const clearInput = () => {
    emitUpdate('');
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filteredOptions.value = contractTermData.value || [];
    }
});
</script>

<template>
    <div
        class="flex flex-col select-none"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <div v-if="label" class="label justify-between">
            <div class="flex items-center">
                {{ label }}
                <span v-if="required" class="required-asterisk">*</span>
                <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
            </div>

            <div class="select-count">
                <template v-if="!isReadonly">
                    <button @click="togglePopup" class="upload-button" :class="{ disabled: disabled }">불러오기</button>
                </template>
            </div>
        </div>

        <div class="relative flex items-start w-full min-h-[2.5rem]" v-if="visible">
            <textarea
                :id="inputId"
                v-model="inputValue"
                class="p-inputtext ps-5 resize-none rounded-md block w-full text-gray-900 bg-gray-60 dark:bg-gray-700 dark:text-white"
                :class="{
                    'error-border': ValidationMsg,
                    'none-border': borderNone,
                    'readonly-Bg': isReadonly
                }"
                :style="height ? { height } : undefined"
                :rows="rows"
                :placeholder="placeholder"
                :required="required"
                :readonly="isReadonly"
                :maxlength="maxlength"
                @input="onInput"
            ></textarea>

            <div class="textarea-placeholder" v-if="!inputValue" v-html="placeholderHtml"></div>

            <!-- 클리어 버튼 -->
            <div class="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button">&times;</button>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay">
                <div class="popup-content">
                    <!-- <h3>선택</h3> -->
                    <input v-model="searchQuery" @input="filterOptions" placeholder="계약사항을 검색하세요" class="search-input" style="margin-top: 1rem" />
                    <div class="options-container">
                        <table class="contractTerm-grid">
                            <thead>
                                <tr>
                                    <th>계약사항명</th>
                                    <th>계약사항</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="option in filteredOptions" :key="option.key" @click="selectValue(option)" class="popup-item">
                                    <td class="ellipsis">{{ option.title }}</td>
                                    <td class="ellipsis left-cell">{{ option.body }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button @click="closePopup" class="close-button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.header-container {
    user-select: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
}

.select-count {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.9;
    display: flex;
    align-items: center;
    margin-right: 0.25rem;
}

.upload-button {
    color: #0038ea;
    cursor: pointer;
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    text-align: center;
    transition: background-color 0.2s;
}

.upload-button:hover {
    font-family: 'SCoreDream600';
}

.upload-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.input-wrapper {
    display: flex;
    flex-direction: column;
}

.contractTerm-grid th {
    position: sticky;
    top: 0;
    background-color: #f2f2f2;
    z-index: 10;
}
.label {
    font-family: 'SCoreDream600';
    display: flex;
    color: #35424a;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus:not([readonly]) {
    border-color: #0038ea;
    outline: none;
}

/* readonly 상태(input[readonly]) 포커스 시 기본 회색 테두리 유지 */
input[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}
.text-right {
    text-align: right;
    padding-right: 6rem;
}

.caution-customer {
    color: #ff0000;
    font-family: 'SCoreDream500';
}

.error-border {
    border: 1px solid red !important;
}
.readonly-Bg {
    background-color: whitesmoke !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem; /* 기본 높이 */
    border: 1px solid #d1d5db; /* 기본 테두리 색상 */
    border-radius: 0.5rem; /* 기본 border-radius */
    /* transition:
        border-color 0.3s ease,
        box-shadow 0.3s ease,
        height 0.3s ease; 애니메이션 */
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
    /* border-radius: 0.5rem !important;*/
    /* height: 2.75rem !important; 포커스 시 높이 증가 */
    /* outline: none !important; 포커스 시 기본 outline 제거 */
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 45rem;
    max-height: 80vh;
    text-align: left;
    min-width: 30rem;
    display: flex;
    flex-direction: column;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 18rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.contractTerm-grid {
    width: 100%;
    border-collapse: collapse;
}

.contractTerm-grid th,
.contractTerm-grid td {
    border: 0.1rem solid #ddd;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    text-align: center;
}

.contractTerm-grid th {
    background-color: #f2f2f2;
    position: sticky;
    top: 0;
    z-index: 10;
}

.contractTerm-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.contractTerm-grid th:hover {
    cursor: default;
}

.contractTerm-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}

.textarea-placeholder {
    position: absolute;
    left: 8px;
    pointer-events: none;
    color: #aaa;
    font-size: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
}
.textarea-placeholder p {
    display: inline !important;
    line-height: 1.5rem;
}

textarea {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
    padding: 0.75rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    resize: none;
}
textarea:focus:not([readonly]) {
    border-color: #0038ea;
    outline: none;
}
textarea[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}

.error-border {
    border: 1px solid red !important;
}
.readonly-Bg {
    background-color: whitesmoke !important;
}
.none-border {
    border: none;
}

/* 스크롤바(선택) */
textarea::-webkit-scrollbar {
    width: 0.5rem;
}
textarea::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}
textarea::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}
textarea::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

/* 본문 셀 */
.left-cell {
    text-align: left;
    padding-left: 1rem;
    max-width: 26rem; /* 필요 시 조정 */
}
</style>
